import { useEffect, useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  buildDayPanel,
  buildIntensityGridFromDraft,
  fetchMaxesMap,
  fetchProgramWarnings,
  muscleKey,
  validateProgramDraft,
  type CatalogMuscleSlug,
  type MuscleAdjacencyWarning,
  type NewProgram,
  type ProgramDraftWarning,
  type TargetItem,
} from "@jackedlogs/shared";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";

import { Card } from "../primitives/Card";
import { Eyebrow } from "../primitives/Eyebrow";
import { makeEditorStyles } from "../ProgramEditor/styles";
import { useBreakpoint } from "../useBreakpoint";
import { makeComposerStyles } from "./composerStyles";
import { makePlanStyles } from "./planComposerStyles";
import { useComposer } from "./useComposer";
import { useEditInPlace } from "./useEditInPlace";
import { useTargetProjection } from "./useTargetProjection";
import { useKindLabels } from "./useKindLabels";
import { ComposerEditLayout } from "./ComposerEditLayout";
import { ComposerMetaBand } from "./ComposerMetaBand";
import { ComposerWarningsPanel } from "./ComposerWarningsPanel";
import { ProgramExerciseEditor } from "./ProgramExerciseEditor";
import { ProgramSpine } from "./ProgramSpine";
import { NewProgramSetup } from "./NewProgramSetup";
import { PlanComposer } from "./PlanComposer";
import { TargetProjectionMatrix } from "./TargetProjectionMatrix";
import { METRIC } from "./useWeeklyTargetsEditor";
import type { ProgramComposerProps } from "./types";

// The scheduler-composer program editor (next/program-editor-redesign.md +
// next/plans-editor-usability.md). It is now the EDIT-IN-PLACE model the
// Plan editor uses: the old compose-form-on-top → preview-at-bottom (which
// forced select-at-bottom → edit-at-top scroll churn) is gone. One shared
// shell (ComposerEditLayout + useEditInPlace): the day's exercises are the
// list, tapping one expands its editor where it sits (ProgramExerciseEditor),
// and the day×week grid + day selector + clone tools ride a spine above the
// list (ProgramSpine). The draft model + save path are unchanged
// (useProgramEditor inside useComposer); only the authoring layout moved.
//
// Create-from-scratch: with neither `program` nor `creationSeed` the
// composer opens on the NewProgramSetup stage (name + weeks/days steppers)
// and seeds itself from it. Single mode (Plan) is delegated to PlanComposer.
export function ProgramComposer(props: ProgramComposerProps) {
  const [seed, setSeed] = useState<NewProgram | null>(null);
  if (props.mode === "single") return <PlanComposer {...props} />;
  if (!props.program && !props.creationSeed && !seed) {
    return (
      <NewProgramSetup
        dark={props.dark}
        onCreate={setSeed}
        onCancel={props.onCancel}
      />
    );
  }
  return (
    <ComposerBody
      {...props}
      creationSeed={props.creationSeed ?? seed ?? undefined}
    />
  );
}

function ComposerBody({
  apiFetch,
  durableWrite,
  dark,
  program,
  creationSeed,
  catalog,
  Picker,
  onSaved,
  onCancel,
  exerciseCatalog,
  unitsPreference,
  athleteTargets,
}: ProgramComposerProps) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const p = theme.palette;
  const cs = useMemo(() => makeComposerStyles(p), [p]);
  const ps = useMemo(() => makePlanStyles(p), [p]);
  const selectStyles = useMemo(() => makeEditorStyles(p), [p]);
  const projStyles = useMemo(() => makeProjectionStyles(theme), [theme]);

  const composer = useComposer({
    durableWrite,
    program,
    creationSeed,
    catalog,
    onSaved,
    mode: "program",
    unitsPreference,
  });
  // setDraft/save are read by ComposerMetaBand off the composer it receives.
  const { draft, busy, error, blocks, form, cellUnit } = composer;
  // Labels follow the unit the cells are ACTUALLY in — the same source the
  // edit table uses. Reading the live preference here would print "KG" over
  // the day panel while the ✎ table below shows "LB" for the same number.
  const kindLabels = useKindLabels(cellUnit);
  const [selectedDay, setSelectedDay] = useState(0);

  // Live warn-only validation (Follow+Publish §4): Save saves ALWAYS. Maxes
  // are fetched ONCE at mount, best-effort — on failure the %MAX check is
  // skipped (null), never guessed. Hard guards stay in save().
  const [maxes, setMaxes] = useState<Map<string, number> | null>(null);
  const [adjacencyWarnOpen, setAdjacencyWarnOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchMaxesMap(apiFetch)
      .then((m) => {
        if (!cancelled) setMaxes(m);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Mount-only on purpose: hosts pass a module-level apiFetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const warnings = useMemo(() => validateProgramDraft(draft, maxes), [draft, maxes]);
  const [warnOpen, setWarnOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  // PE-21 — server-computed muscle-adjacency advisory (packages/api's
  // programWarnings), fetched once at mount alongside maxes: best-effort,
  // never blocks. Existing programs only (`program?.id`) — a brand-new
  // create-from-scratch draft has no id to fetch yet.
  const [adjacencyWarnings, setAdjacencyWarnings] = useState<
    readonly MuscleAdjacencyWarning[]
  >([]);
  useEffect(() => {
    const id = program?.id;
    if (!id) {
      setAdjacencyWarnings([]);
      return;
    }
    let cancelled = false;
    fetchProgramWarnings(apiFetch, id)
      .then((w) => {
        if (!cancelled) setAdjacencyWarnings(w);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Mount-only per program id, mirrors the maxes effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?.id]);

  const grid = useMemo(() => buildIntensityGridFromDraft(draft), [draft]);

  const targetProjection = useTargetProjection({
    draft,
    cellUnit,
    exerciseCatalog,
    maxes,
    athleteTargets,
  });
  const entries = useMemo(
    () => buildDayPanel(blocks, selectedDay, kindLabels),
    [blocks, selectedDay, kindLabels],
  );
  const resolveName = useMemo(() => {
    const bySlug = new Map(catalog.map((c) => [c.slug, c.name]));
    return (id: string, snapshot?: string) => snapshot ?? bySlug.get(id) ?? id;
  }, [catalog]);

  // The read-only prescription projection labels a target row by muscle name
  // (i18n) or the exercise's resolved name — the same axis split the meter uses.
  const targetLabel = useMemo(
    () =>
      (item: TargetItem): string =>
        item.axis === "muscle"
          ? t(muscleKey(item.key as CatalogMuscleSlug))
          : resolveName(item.key),
    [t, resolveName],
  );

  // A fresh exercise lands on the day you're looking at (per-day list model).
  const addDays = useMemo(() => new Set([selectedDay]), [selectedDay]);
  const ip = useEditInPlace({ composer, entries, day: selectedDay, addDays });

  // Switching day commits + closes the open editor first (its editingIdx is
  // relative to the old day's entries). A visible dup conflict can't be
  // committed away → keep the user on this day to resolve it before leaving.
  const selectDay = (d: number) => {
    if (composer.formConflict) return;
    ip.collapse();
    setSelectedDay(d);
  };

  // Only the CURRENT form's members: the same exercise on another day or
  // week is a legitimate second block, and since LG-41 a second copy inside
  // the block is legitimate too — a Slot carries its own id (program.ts:150),
  // so two slots with one slug are distinct rows all the way down.
  const presentSlugs = useMemo(() => form.members.map((m) => m.exerciseId), [form.members]);

  const warningLabel = (w: ProgramDraftWarning): string =>
    w.code === "emptyCell"
      ? t("composer.checkEmptyCell", {
          w: w.week + 1,
          d: w.dayIdx + 1,
          defaultValue: "W{{w}}·D{{d}} has no exercises",
        })
      : t("composer.checkPctNoMax", {
          name: resolveName(w.exerciseId),
          defaultValue: "{{name}} uses %MAX without a saved MAX",
        });

  // ME-3 — read-only prescription projection: what this block prescribes each
  // week (from the program's structure) measured against the athlete's own
  // weekly targets. A sibling surface beside the meta card, never an editor —
  // targets are set on Me now, not on the program. Absent when the athlete has
  // no scoreable targets or the draft can't yet be projected.
  const prescriptionSection = targetProjection ? (
    <Card theme={theme} style={{ gap: theme.spacing.s2 }}>
      <Eyebrow theme={theme}>
        {t("composer.prescriptionEyebrow", { defaultValue: "Prescription" })}
      </Eyebrow>
      <Text style={projStyles.sub}>
        {t("composer.prescriptionSub", {
          defaultValue:
            "What this block prescribes each week, measured against your goals.",
        })}
      </Text>
      <TargetProjectionMatrix
        theme={theme}
        projection={targetProjection}
        unitsPreference={unitsPreference ?? "kg"}
        labelFor={targetLabel}
        metricLabel={(m) => t(METRIC[m].chip)}
      />
    </Card>
  ) : null;

  const warningsPanel = (
    <ComposerWarningsPanel
      theme={theme}
      cs={cs}
      warnings={warnings}
      warningLabel={warningLabel}
      warnOpen={warnOpen}
      setWarnOpen={setWarnOpen}
      adjacencyWarnings={adjacencyWarnings}
      adjacencyWarnOpen={adjacencyWarnOpen}
      setAdjacencyWarnOpen={setAdjacencyWarnOpen}
    />
  );

  const spine = (
    <ProgramSpine
      grid={grid}
      selectedDay={selectedDay}
      onSelectDay={selectDay}
      dark={dark}
      wide={wide}
      dayCount={composer.dayCount}
      weekCount={composer.weekCount}
      hasContent={entries.length > 0}
      resolveName={resolveName}
      cloneDay={(toDay) => {
        const r = composer.cloneDay(selectedDay, toDay);
        if (r.ok) setSelectedDay(toDay);
        return r;
      }}
      cloneWeek={composer.cloneWeek}
      addDay={composer.addDay}
      // Commit/close the open editor BEFORE removing a day: removeSession
      // re-indexes every later day, so a still-open edit would otherwise flush
      // against stale day indices (silent no-op / wrong day). Same collapse
      // guard selectDay uses when switching days.
      removeDay={(idx) => {
        ip.collapse();
        composer.removeDay(idx);
      }}
      changeCycleWeeks={composer.changeCycleWeeks}
      cs={cs}
      ps={ps}
    />
  );

  const editor = (
    <ProgramExerciseEditor
      composer={composer}
      onReplaceMember={(idx) => ip.setPicker({ replace: idx })}
      resolveName={resolveName}
      cs={cs}
      ps={ps}
      selectStyles={selectStyles}
      theme={theme}
      onCollapse={ip.collapse}
      onClone={ip.cloneCurrent}
      onRemove={ip.removeCurrent}
    />
  );

  const picker = (
    <Picker
      visible={ip.picker !== null}
      onClose={() => ip.setPicker(null)}
      onAddMany={ip.onAddMany}
      onCreateSuperset={({ addSlugs }) => ip.onCreateSuperset(addSlugs)}
      presentSlugs={presentSlugs}
      presentTreatment="mark-program"
    />
  );

  const dayName = grid.days[selectedDay]?.name ?? "";

  return (
    <ComposerEditLayout
      cs={cs}
      ps={ps}
      theme={theme}
      wide={wide}
      busy={busy}
      backLabel={t("program.backToList", { defaultValue: "Back to programs" })}
      title={draft.name || t("program.newTitle", { defaultValue: "New program" })}
      onCancel={onCancel}
      onSave={ip.handleSave}
      error={error}
      warnings={warningsPanel}
      metaBand={
        <View style={{ gap: theme.spacing.s4 }}>
          <ComposerMetaBand
            composer={composer}
            cs={cs}
            theme={theme}
            showLiveEditHint={!!program}
            warnings={warnings}
            warningLabel={warningLabel}
            publishOpen={publishOpen}
            setPublishOpen={setPublishOpen}
          />
          {prescriptionSection}
        </View>
      }
      spine={spine}
      sectionLabel={
        dayName
          ? `${dayName} · ${t("plans.exercisesLabel", { defaultValue: "Exercises" })}`
          : t("plans.exercisesLabel", { defaultValue: "Exercises" })
      }
      emptyHint={t("composer.dayEmpty", {
        defaultValue: "No exercises on this day yet. Add one to build it.",
      })}
      selectPrompt={t("plans.selectExercisePrompt", {
        defaultValue: "Select an exercise to edit its sets.",
      })}
      entries={entries}
      resolveName={resolveName}
      editingIdx={ip.editingIdx}
      editing={ip.editing}
      composingNew={ip.composingNew}
      hasForm={ip.hasForm}
      editor={editor}
      onOpenEntry={ip.openEntry}
      onOpenAdd={ip.addExercise}
      addLabel={t("composer.addExercises", { defaultValue: "Add exercises" })}
      picker={picker}
    />
  );
}

function makeProjectionStyles(theme: ReturnType<typeof resolveTheme>) {
  const { palette: p, typography: ty } = theme;
  return StyleSheet.create({
    sub: {
      color: p.ink2,
      fontSize: ty.sizes.base,
      lineHeight: ty.sizes.base * 1.4,
    },
  });
}
